/**
 * ActiveCallContext
 *
 * Holds the active call state ABOVE the router so that navigating between
 * pages never unmounts the call UI. The call continues in a persistent
 * overlay (full-screen or minimized pill).
 *
 * Also handles incoming-call ringing with a ringtone + accept/decline UI.
 */
import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import type { Call } from "@stream-io/video-react-sdk";
import { useStreamChat } from "@/features/chat/context/StreamChatContext";
import { useAuth } from "@/features/auth/context/AuthContext";
import { sendCallLogMessage } from "@/features/call/utils/callLog";
import { Toast } from "@/shared/components/feedback/Toast";
import { deviceErrorMessageKey } from "@/features/call/utils/deviceErrors";

// ─── Types ───
export interface CallPeer {
  userId: string;
  name: string;
  image?: string;
  company?: string;
  batchNo?: string;
  visaType?: string;
  userType?: string;
}

export type CallViewMode = "full" | "pip";

interface ActiveCallState {
  /** The SDK Call object (null when idle) */
  call: Call | null;
  /** Info about the primary other party (first peer or only peer) */
  peer: CallPeer | null;
  /** All peers in a group call */
  peers: CallPeer[];
  /** Whether this is a group call (>2 participants) */
  isGroupCall: boolean;
  /** Channel ID for call-log messages */
  channelId?: string;
  /** Whether the local user initiated the call */
  isOutgoing: boolean;
  /** Whether the incoming call has been accepted (always true for outgoing) */
  isAccepted: boolean;
  /** full-screen or picture-in-picture pill */
  viewMode: CallViewMode;
  /** Whether the current user created the call (has End for All permission) */
  isCallCreator: boolean;
  /** Whether the creator has locked all participants from unmuting (creator's view) */
  allMicsMuted: boolean;
  /** Whether this participant has been mute-locked by the creator */
  isLockedByCreator: boolean;
}

interface ActiveCallActions {
  /** Start an outgoing call (1:1) */
  startCall: (callId: string, peer: CallPeer, channelId?: string) => Promise<void>;
  /** Start a group call with multiple peers */
  startGroupCall: (callId: string, peers: CallPeer[], channelId?: string) => Promise<void>;
  /** Handle an incoming ring event */
  handleIncomingRing: (callId: string, peer: CallPeer, isGroup?: boolean, channelId?: string) => void;
  /** Accept the incoming call */
  acceptCall: () => Promise<void>;
  /** Decline the incoming call */
  declineCall: () => Promise<void>;
  /** Leave the call without ending it for others (group) or end it (1:1) */
  leaveCall: () => void;
  /** End the call for ALL participants (only available to call creator) */
  endCallForAll: () => void;
  /** Join an already-active group call without ringing */
  joinCall: (callId: string, channelId?: string) => Promise<void>;
  /** Toggle between full and pip */
  toggleViewMode: () => void;
  /** Expand to full-screen */
  expandCall: () => void;
  /** Creator-only: toggle mute lock for all participants */
  toggleAllMuteLock: () => void;
  /** Show a transient toast notification (non-blocking, replaces alert()) */
  showToast: (message: string, type?: "success" | "error" | "info") => void;
}

type ActiveCallContextType = ActiveCallState & ActiveCallActions;

const ActiveCallContext = createContext<ActiveCallContextType | null>(null);

export const useActiveCall = () => {
  const ctx = useContext(ActiveCallContext);
  if (!ctx) throw new Error("useActiveCall must be inside ActiveCallProvider");
  return ctx;
};

// ─── Provider ───
export const ActiveCallProvider = ({ children }: { children: React.ReactNode }) => {
  const { videoClient, chatClient } = useStreamChat();
  const { userData } = useAuth();
  const { t } = useTranslation();

  const [toast, setToast] = useState<{ message: string; type: "success" | "error" | "info" } | null>(null);
  const showToast = useCallback(
    (message: string, type: "success" | "error" | "info" = "info") => setToast({ message, type }),
    [],
  );

  const [call, setCall] = useState<Call | null>(null);
  const [peer, setPeer] = useState<CallPeer | null>(null);
  const [peers, setPeers] = useState<CallPeer[]>([]);
  const [isGroupCall, setIsGroupCall] = useState(false);
  const [channelId, setChannelId] = useState<string | undefined>();
  const [isOutgoing, setIsOutgoing] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [viewMode, setViewMode] = useState<CallViewMode>("full");
  const [isCallCreator, setIsCallCreator] = useState(false);
  const [allMicsMuted, setAllMicsMuted] = useState(false);
  const [isLockedByCreator, setIsLockedByCreator] = useState(false);

  // Ref mirror of isOutgoing — readable inside effect closures without stale state
  const isOutgoingRef = useRef(false);

  // Refs for call-log tracking
  const callRef = useRef<Call | null>(null);
  const callLogSentRef = useRef(false);
  const remoteConnectedRef = useRef(false);
  const callStartTimeRef = useRef<number>(Date.now());
  // Ref mirror of isAccepted — readable inside effect closures without stale state
  const isAcceptedRef = useRef(false);
  // Refs for stable access in event-listener closures
  const channelIdRef = useRef<string | undefined>(undefined);
  const chatClientRef = useRef(chatClient);
  chatClientRef.current = chatClient;
  const isGroupCallRef = useRef(false);

  // ─── Send call log (fire-once) ───
  const sendCallLog = useCallback(
    (wasConnected: boolean) => {
      if (callLogSentRef.current) return;
      if (!isOutgoing) return; // only the caller sends the log
      if (!chatClient || !userData?.id || !peer?.userId) return;

      callLogSentRef.current = true;

      const elapsedSec = Math.floor((Date.now() - callStartTimeRef.current) / 1000);
      const mins = Math.floor(elapsedSec / 60);
      const secs = elapsedSec % 60;
      const duration = `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;

      // For group calls always send "Call ended · Duration" regardless of
      // whether anyone connected — "Missed call" is not meaningful in a group.
      const wasConnectedForLog = isGroupCallRef.current ? true : wasConnected;

      sendCallLogMessage({
        chatClient,
        myUserId: userData.id.toString(),
        otherUserId: peer.userId,
        otherUserName: peer.name,
        wasConnected: wasConnectedForLog,
        duration,
        channelId,
      });
    },
    [chatClient, userData?.id, peer?.userId, peer?.name, isOutgoing, channelId],
  );

  // ─── Cleanup helper ───
  const resetState = useCallback(() => {
    setCall(null);
    setPeer(null);
    setPeers([]);
    setIsGroupCall(false);
    setChannelId(undefined);
    isOutgoingRef.current = false;
    setIsOutgoing(false);
    setIsAccepted(false);
    setViewMode("full");
    setIsCallCreator(false);
    setAllMicsMuted(false);
    setIsLockedByCreator(false);
    callRef.current = null;
    callLogSentRef.current = false;
    remoteConnectedRef.current = false;
    isAcceptedRef.current = false;
    channelIdRef.current = undefined;
    isGroupCallRef.current = false;
  }, []);

  // ─── Actions ───

  const startCall = useCallback(
    async (callId: string, peerInfo: CallPeer, chId?: string) => {
      if (!videoClient || !userData?.id) return;

      const _call = videoClient.call("default", callId);

      await _call.getOrCreate({
        ring: true,
        data: {
          members: [
            { user_id: userData.id.toString(), role: "admin" },
            { user_id: peerInfo.userId, role: "user" },
          ],
        },
      });
      // Disable camera BEFORE join() so the SDK never publishes a video track.
      // Calling disable() after join() causes a brief camera-on flash.
      await _call.camera.disable();
      await _call.join({ create: false });

      callRef.current = _call;
      callStartTimeRef.current = Date.now();
      callLogSentRef.current = false;
      remoteConnectedRef.current = false;
      isAcceptedRef.current = true;
      channelIdRef.current = chId;
      isGroupCallRef.current = false;

      setPeer(peerInfo);
      setPeers([peerInfo]);
      setIsGroupCall(false);
      setChannelId(chId);
      isOutgoingRef.current = true;
      setIsOutgoing(true);
      setIsAccepted(true);
      setIsCallCreator(true);
      setViewMode("pip");
      setCall(_call);
    },
    [videoClient, userData?.id],
  );

  const startGroupCall = useCallback(
    async (callId: string, groupPeers: CallPeer[], chId?: string) => {
      if (!videoClient || !userData?.id || groupPeers.length === 0) return;

      const _call = videoClient.call("default", callId);

      const members = [
        { user_id: userData.id.toString(), role: "admin" as const },
        ...groupPeers.map((p) => ({
          user_id: p.userId,
          role: "user" as const,
        })),
      ];

      await _call.getOrCreate({
        ring: true,
        data: { members, custom: { isGroupCall: true, channelId: chId } },
      });
      // Disable camera BEFORE join() so the SDK never publishes a video track.
      // Calling disable() after join() causes a brief camera-on flash.
      await _call.camera.disable();
      await _call.join({ create: false });

      // Track activeCallId on channel so other members can join later
      if (chId && chatClient) {
        const ch = chatClient.channel("messaging", chId);
        ch.updatePartial({ set: { activeCallId: callId } } as any).catch(console.error);
      }

      callRef.current = _call;
      callStartTimeRef.current = Date.now();
      callLogSentRef.current = false;
      remoteConnectedRef.current = false;
      isAcceptedRef.current = true;
      channelIdRef.current = chId;
      isGroupCallRef.current = true;

      setPeer(groupPeers[0]);
      setPeers(groupPeers);
      setIsGroupCall(true);
      setChannelId(chId);
      isOutgoingRef.current = true;
      setIsOutgoing(true);
      setIsAccepted(true);
      setIsCallCreator(true);
      setViewMode("pip");
      setCall(_call);
    },
    [videoClient, userData?.id, chatClient],
  );

  const handleIncomingRing = useCallback(
    (callId: string, peerInfo: CallPeer, isGroup?: boolean, chId?: string) => {
      if (!videoClient) return;
      // If already in a call, ignore
      if (callRef.current) {
        console.log("[ActiveCall] Already in a call, ignoring incoming ring");
        return;
      }

      const _call = videoClient.call("default", callId);

      callRef.current = _call;
      callStartTimeRef.current = Date.now();
      callLogSentRef.current = false;
      remoteConnectedRef.current = false;
      isGroupCallRef.current = isGroup ?? false;
      channelIdRef.current = chId;

      setPeer(peerInfo);
      setIsGroupCall(isGroup ?? false);
      setChannelId(chId);
      setIsCallCreator(false); // receiver is never the creator
      isOutgoingRef.current = false;
      setIsOutgoing(false);
      setIsAccepted(false);
      setViewMode("full");
      setCall(_call);
    },
    [videoClient],
  );

  const acceptCall = useCallback(async () => {
    if (!callRef.current) return;
    // Set ref BEFORE join() so that any call.rejected event that races with
    // the auto_cancel_timeout won't tear down an already-accepted call.
    isAcceptedRef.current = true;
    // Set state IMMEDIATELY so IncomingRingScreen unmounts (stops ringtone)
    // before the async join/camera/mic operations that take 3-5 seconds.
    setIsAccepted(true);
    // Disable camera BEFORE join() so the SDK never publishes a video track.
    // Calling disable() after join() causes a brief camera-on flash.
    await callRef.current.camera.disable();
    await callRef.current.join();
    // Honor a mute-lock the creator set before we joined — the custom event
    // is transient, so late joiners must read the persisted flag instead.
    const micsLocked =
      (callRef.current.state.custom as any)?.micsLocked === true;
    if (micsLocked) {
      setIsLockedByCreator(true);
      await callRef.current.microphone.disable();
    } else {
      // The call is already joined at this point; a blocked or missing
      // microphone must not reject accept() (which nothing awaits) — stay in
      // the call muted and tell the user why.
      try {
        await callRef.current.microphone.enable();
      } catch (err) {
        console.error("[ActiveCall] Microphone enable on accept failed:", err);
        showToast(t(deviceErrorMessageKey(err, "microphone") ?? "call.mic_toggle_failed"), "error");
      }
    }
  }, [showToast, t]);

  const declineCall = useCallback(async () => {
    if (!callRef.current) return;
    try {
      await callRef.current.reject();
    } catch (e) {
      console.error("[ActiveCall] Decline error:", e);
    }
    // reject() closes media before we unmount
    resetState();
  }, [resetState]);

  // ─── Leave call (local only — does NOT end for others) ───
  // For group calls: if the local user is the last participant, clear the
  // activeCallId on the channel so the "Call in progress" banner disappears.
  const leaveCall = useCallback(() => {
    if (callRef.current) {
      const c = callRef.current;
      const cs = c.state.callingState;
      if (cs !== "left" && cs !== "idle") {
        // Check if we're the last participant — if so, clean up the channel
        const remoteCount = c.state.participants.filter(
          (p) => !p.isLocalParticipant,
        ).length;
        if (remoteCount === 0 && isGroupCallRef.current && channelIdRef.current && chatClientRef.current) {
          const ch = chatClientRef.current.channel("messaging", channelIdRef.current);
          ch.updatePartial({ unset: ["activeCallId"] } as any).catch(console.error);
        }
        c.leave()
          .catch(console.error)
          .finally(() => resetState());
        return;
      }
    }
    resetState();
  }, [resetState]);

  // ─── End call for ALL participants ───
  const endCallForAll = useCallback(() => {
    sendCallLog(remoteConnectedRef.current);
    // Clear activeCallId from channel (group calls only)
    if (isGroupCallRef.current && channelIdRef.current && chatClient) {
      const ch = chatClient.channel("messaging", channelIdRef.current);
      ch.updatePartial({ unset: ["activeCallId"] } as any).catch(console.error);
    }
    if (callRef.current) {
      const c = callRef.current;
      const cs = c.state.callingState;
      if (cs !== "left" && cs !== "idle") {
        // End for all first, THEN reset state so the SDK can close media tracks
        // before we unmount the StreamCall tree (avoids orphaned microphone).
        c.endCall()
          .catch(() => c.leave().catch(console.error))
          .finally(() => resetState());
        return; // resetState called in finally
      }
    }
    resetState();
  }, [sendCallLog, resetState, chatClient]);

  // ─── Join an already-active group call (no ringing) ───
  const joinCall = useCallback(
    async (callId: string, chId?: string) => {
      if (!videoClient || !userData?.id) return;
      // Guard: already in a call
      if (callRef.current) {
        console.warn("[ActiveCall] Already in a call, cannot join another");
        return;
      }

      const _call = videoClient.call("default", callId);

      // Fetch the existing call WITHOUT creating it. getOrCreate({ ring: false })
      // would silently re-create a dead call (e.g. stale activeCallId after the
      // creator's browser crashed) and strand the user alone in it. get() throws
      // for a missing call, which lets callers unset the stale activeCallId.
      await _call.get();
      if (_call.state.endedAt) {
        throw new Error("Call has already ended");
      }
      // Disable camera BEFORE join() so the SDK never publishes a video track.
      // Calling disable() after join() causes a brief camera-on flash.
      await _call.camera.disable();
      await _call.join({ create: false });

      // Build peer info from current call participants
      const participants = _call.state.participants;
      const remotePeers: CallPeer[] = participants
        .filter((p) => !p.isLocalParticipant)
        .map((p) => ({
          userId: p.userId,
          name: p.name || p.userId,
          image: p.image,
        }));

      // Detect if the current user is the original call creator (e.g. re-joining
      // after leaving). This preserves the "End for All" permission even on re-join.
      const createdById = (_call.state.createdBy as any)?.id;
      const isCreator = createdById === userData!.id.toString();

      // Honor a persisted mute-lock (set via toggleAllMuteLock) — late joiners
      // never received the transient custom event, so read the flag instead.
      const micsLocked = (_call.state.custom as any)?.micsLocked === true;
      if (micsLocked && !isCreator) {
        await _call.microphone.disable();
      }

      callRef.current = _call;
      callStartTimeRef.current = Date.now();
      callLogSentRef.current = false;
      remoteConnectedRef.current = true; // already in a call with others
      isAcceptedRef.current = true;
      channelIdRef.current = chId;
      isGroupCallRef.current = true;

      setPeer(remotePeers[0] || { userId: "group", name: "Group Call" });
      setPeers(remotePeers);
      setIsGroupCall(true);
      setChannelId(chId);
      isOutgoingRef.current = false;
      setIsOutgoing(false);
      setIsAccepted(true);
      setIsCallCreator(isCreator);
      setIsLockedByCreator(micsLocked && !isCreator);
      // Creator re-joining sees their lock toggle in the correct position
      setAllMicsMuted(micsLocked);
      setViewMode("pip");
      setCall(_call);
    },
    [videoClient, userData?.id],
  );

  const toggleViewMode = useCallback(() => {
    setViewMode((prev) => (prev === "full" ? "pip" : "full"));
  }, []);

  const expandCall = useCallback(() => {
    setViewMode("full");
  }, []);

  const toggleAllMuteLock = useCallback(() => {
    if (!callRef.current) return;
    setAllMicsMuted((prev) => {
      const nextLocked = !prev;
      const c = callRef.current!;
      // Custom event gives currently-joined members instant delivery, but is
      // transient — persist the lock in call custom data so members who join
      // AFTER the lock (banner late-join, ring accept) still honor it.
      c.sendCustomEvent({ type: "mute-lock-all", data: { locked: nextLocked } });
      c.update({
        custom: { ...c.state.custom, micsLocked: nextLocked },
      }).catch(console.error);
      return nextLocked;
    });
  }, []);

  // ─── Listen for mute-lock-all custom events (participants receive this) ───
  useEffect(() => {
    if (!call) return;
    const unsub = call.on("custom", (event: any) => {
      if (event.custom?.type === "mute-lock-all") {
        // The creator also receives their own broadcast — the lock must never
        // apply to the creator (their controls stay usable).
        if (event.user?.id === call.currentUserId) return;
        const locked = event.custom?.data?.locked === true;
        setIsLockedByCreator(locked);
        // Locking must actually silence the mic, not just disable the
        // unmute button — otherwise a hot mic keeps transmitting.
        if (locked) {
          call.microphone.disable().catch(console.error);
        }
      }
    });
    return () => unsub();
  }, [call]);

  // ─── Best-effort cleanup on tab close ───
  // Without this, closing the tab mid-call skips leaveCall's "last participant"
  // cleanup and can leave a stale activeCallId on the channel (stale banner).
  // The requests may not complete during unload — joinCall's get() guard is the
  // backstop — but firing them catches most cases.
  useEffect(() => {
    if (!call) return;
    const handleBeforeUnload = () => {
      leaveCall();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [call, leaveCall]);

  // ─── Listen for incoming call.ring events ───
  useEffect(() => {
    if (!videoClient || !userData?.id) return;

    const unsub = videoClient.on("call.ring", (event: any) => {
      const callData = event.call;
      if (!callData?.id) return;

      console.log("[ActiveCall] call.ring received:", callData.id);

      // Guard: already handling a call.
      if (callRef.current) {
        if (callRef.current.id === callData.id) {
          // Duplicate call.ring for the call we're already ringing/answering.
          // This can happen on WebSocket reconnects — the SDK re-delivers the
          // ring event. Silently ignore it; rejecting would kill the call the
          // callee is currently trying to answer.
          console.log("[ActiveCall] Duplicate call.ring for current call — ignoring:", callData.id);
          return;
        }
        // A genuinely different call while we're busy — reject immediately so
        // the caller gets feedback instead of waiting the full 30-second timeout.
        console.log("[ActiveCall] Already in a call — sending busy rejection for:", callData.id);
        const busyCall = videoClient.call("default", callData.id);
        busyCall.reject("busy").catch(console.error);
        return;
      }

      // Detect group call: check custom flag first (reliable), fall back to member count
      const isGroup =
        event.call?.custom?.isGroupCall === true ||
        (event.members?.length || 0) > 2;

      // Extract channelId from call custom data so ring-accepted members
      // can participate in activeCallId cleanup on the channel.
      const ringChannelId = event.call?.custom?.channelId as string | undefined;

      let caller = event.user;
      if (!caller) {
        const callerMember = event.members?.find(
          (m: any) => m.user?.id !== userData.id.toString(),
        );
        caller = callerMember?.user;
      }

      handleIncomingRing(callData.id, {
        userId: caller?.id || "unknown",
        name: caller?.name || "Unknown User",
        image: caller?.image,
        company: caller?.custom?.company_name || caller?.custom?.company,
        batchNo: caller?.custom?.batch_no,
        userType: caller?.custom?.user_type,
      }, isGroup, ringChannelId);
    });

    return () => {
      unsub?.();
    };
  }, [videoClient, userData?.id, handleIncomingRing]);

  // ─── Stable refs so effects never re-run due to callback identity changes ───
  const endCallForAllRef = useRef(endCallForAll);
  endCallForAllRef.current = endCallForAll;
  const sendCallLogRef = useRef(sendCallLog);
  sendCallLogRef.current = sendCallLog;
  const resetStateRef = useRef(resetState);
  resetStateRef.current = resetState;
  const tRef = useRef(t);
  tRef.current = t;

  // ─── Listen for call rejection / ending at the provider level ───
  // These listeners must live here (not inside StreamCall components) because:
  //   1. call.rejected fires BEFORE the call is joined, so StreamCall hooks
  //      may not be mounted yet.
  //   2. The provider owns the Call object lifetime — it must clean up when
  //      the remote party rejects or the call ends server-side.
  // Uses *Ref callbacks so this effect is stable and never re-registers
  // listeners mid-call when sendCallLog/resetState change identity.
  useEffect(() => {
    const currentCall = callRef.current;
    if (!currentCall) return;

    const safeLeaveAndReset = (logResult: boolean, clearChannelBanner: boolean) => {
      sendCallLogRef.current(logResult);
      // Only clear activeCallId for truly terminal events (call.ended /
      // call.session_ended). Rejections from a declining recipient must
      // NOT remove the banner — the call is still alive for others.
      if (clearChannelBanner && isGroupCallRef.current && channelIdRef.current && chatClientRef.current) {
        const ch = chatClientRef.current.channel("messaging", channelIdRef.current);
        ch.updatePartial({ unset: ["activeCallId"] } as any).catch(console.error);
      }
      const cs = currentCall.state.callingState;
      if (cs !== "left" && cs !== "idle") {
        currentCall.leave()
          .catch(console.error)
          .finally(() => resetStateRef.current());
      } else {
        resetStateRef.current();
      }
    };

    // Recipient pressed "Decline" — Stream broadcasts to ALL participants.
    // GUARD 1: ignore if we've already accepted (joined) the call.
    // GUARD 2: in group calls, ignore rejections from OTHER members —
    //          B declining must not kill C's ring. Only process our own
    //          rejection (triggered by our declineCall) or 1:1 rejections.
    const unsubRejected = currentCall.on("call.rejected", (event: any) => {
      console.log("[ActiveCall] call.rejected received", event);
      if (isAcceptedRef.current) {
        console.log("[ActiveCall] call.rejected ignored — call already accepted");
        return;
      }
      // If the callee rejected because they are already in another call,
      // show an immediate "busy" alert to the caller instead of silently
      // ending. This saves the caller from waiting the full 30-second timeout.
      const rejectionReason = event.reason as string | undefined;
      if (isOutgoingRef.current && rejectionReason === "busy") {
        console.log("[ActiveCall] call.rejected(busy) — callee is on another call");
        sendCallLogRef.current(false);
        showToast(tRef.current("call.user_busy"), "info");
        resetStateRef.current();
        return;
      }

      if (isGroupCallRef.current) {
        const rejectorId = event.user?.id;
        const myId = currentCall.currentUserId;
        if (rejectorId && myId && rejectorId !== myId) {
          console.log("[ActiveCall] call.rejected by another member — ignoring");
          return;
        }
      }
      safeLeaveAndReset(false, false); // rejection — do NOT clear banner
    });

    // Call ended server-side or by the other party via endCall()
    const unsubEnded = currentCall.on("call.ended", (event: any) => {
      console.log("[ActiveCall] call.ended received", event);
      safeLeaveAndReset(remoteConnectedRef.current, true); // terminal — clear banner
    });

    // Session ended (local user left). Do NOT clear the activeCallId banner here —
    // Stream fires call.session_ended on the local device when the local user leaves,
    // even if other participants are still in the call. Clearing the banner would
    // remove the "Join Call" button for everyone else.
    // The banner is only cleared by: call.ended, endCallForAll(), or leaveCall()
    // when the local user is the last participant.
    const unsubSessionEnded = currentCall.on("call.session_ended", (event: any) => {
      console.log("[ActiveCall] call.session_ended received", event);
      safeLeaveAndReset(remoteConnectedRef.current, false); // leave locally, keep banner
    });

    // NOTE: call.session_participant_left is intentionally NOT handled here.
    // Stream fires it whenever any member transitions between session phases,
    // including when the callee moves from ringing → joined. Any delay-based
    // guard races with participants$ and causes false call termination.
    // call.rejected / call.ended / call.session_ended cover all real endings.

    return () => {
      unsubRejected();
      unsubEnded();
      unsubSessionEnded();
    };
  }, [call]); // refs keep handlers current — stable on call object only

  // ─── Track remoteConnected via participants$ ───
  // Also auto-cleans up group calls when all remote participants leave.
  // Uses a debounce to avoid false positives from transient intermediate states
  // (e.g. participant object rebuilds during join transitions).
  useEffect(() => {
    if (!call) return;

    let emptyTimer: ReturnType<typeof setTimeout> | null = null;

    const sub = call.state.participants$.subscribe((participants) => {
      const hasRemote = participants.some((p) => !p.isLocalParticipant);
      if (hasRemote) {
        remoteConnectedRef.current = true;
        // Cancel any pending cleanup — remotes are back
        if (emptyTimer) {
          clearTimeout(emptyTimer);
          emptyTimer = null;
        }
      } else if (remoteConnectedRef.current && isGroupCallRef.current) {
        // All remotes left after being connected — debounce 5s to confirm
        // it's not a transient state, then clean up.
        if (!emptyTimer) {
          emptyTimer = setTimeout(() => {
            // Re-verify participants are still empty
            const currentRemote = call.state.participants.filter(
              (p) => !p.isLocalParticipant,
            );
            if (currentRemote.length === 0) {
              console.log("[ActiveCall] All participants left group call — auto-ending");
              endCallForAllRef.current();
            }
            emptyTimer = null;
          }, 5000);
        }
      }
    });

    return () => {
      if (emptyTimer) clearTimeout(emptyTimer);
      sub.unsubscribe();
    };
  }, [call]);

  // ─── Expose remoteConnectedRef setter for the overlay ───
  // (Stored on the context so the overlay can mark it)
  const markRemoteConnected = useCallback(() => {
    remoteConnectedRef.current = true;
  }, []);

  const value: ActiveCallContextType & { markRemoteConnected: () => void; sendCallLog: (c: boolean) => void } = {
    call,
    peer,
    peers,
    isGroupCall,
    channelId,
    isOutgoing,
    isAccepted,
    viewMode,
    isCallCreator,
    allMicsMuted,
    isLockedByCreator,
    startCall,
    startGroupCall,
    handleIncomingRing,
    acceptCall,
    declineCall,
    leaveCall,
    endCallForAll,
    joinCall,
    toggleViewMode,
    expandCall,
    toggleAllMuteLock,
    showToast,
    markRemoteConnected,
    sendCallLog,
  };

  return (
    <ActiveCallContext.Provider value={value}>
      {children}
      {toast && (
        <div className="fixed inset-0 z-[10000] pointer-events-none [&>*]:pointer-events-auto">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={4000} />
        </div>
      )}
    </ActiveCallContext.Provider>
  );
};
